import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '@/lib/enhancedAxiosInstance';
import { paths } from '@/lib/apiPaths';
import { ENABLE_ATTRIBUTE_GROUPS } from '@/config/featureFlags';

import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAttributeGroupsTranslations } from '@/hooks/useAttributeGroupsTranslations';

import { useInvalidateAttributeGroupsCache } from '@/hooks/useGlobalAttributes';
import { logError, logDebug } from '@/utils/errorHandler';

// UI Components
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, PlusCircle, AlertCircle, Pencil, Trash, GripVertical, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';

// Types
interface Attribute {
  id: number;
  code: string;
  label: string;
  data_type: string;
}

interface AttributeGroupItem {
  id?: number;
  attribute: number;
  order: number;
}

interface AttributeGroup {
  id: number;
  name: string;
  order: number;
  items: AttributeGroupItem[];
  version?: number;
  created_at?: string;
  updated_at?: string;
  updated_by?: number;
}

// Form values interface
interface GroupFormValues {
  name: string;
  order: number;
  items: AttributeGroupItem[];
}

// Import our utility functions at the top of the file
import {
  updateAttributeGroup,
  reorderAttributeGroupItems,
  addAttributeToGroup,
  removeAttributeFromGroup,
  createAttributeGroup,
  deleteAttributeGroup,
} from '../api/attributeGroupApi';

// Sortable item wrapper for drag-and-drop
const SortableAttributeItem = ({
  item,
  attributes,
  onRemove,
}: {
  item: AttributeGroupItem;
  attributes: Attribute[];
  onRemove: (id: number) => void;
}) => {
  const attribute = attributes.find(a => a.id === item.attribute);
  const {
    attributes: sortableAttributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    id: item.id?.toString() || `new-${item.attribute}`,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  if (!attribute) return null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="mb-2 flex items-center justify-between rounded-md border bg-card p-2"
    >
      <div className="flex items-center">
        <div className="cursor-move p-1" {...listeners} {...sortableAttributes}>
          <GripVertical className="size-4 text-gray-500" />
        </div>
        <span className="ml-2">{attribute.label}</span>
        <Badge variant="secondary" className="ml-2">
          {attribute.data_type}
        </Badge>
      </div>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => onRemove(attribute.id)}
        className="text-gray-500 hover:text-red-500"
      >
        <X className="size-4" />
      </Button>
    </div>
  );
};

const AttributeGroupsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const i18n = useAttributeGroupsTranslations();
  const invalidateAttributeGroupsCache = useInvalidateAttributeGroupsCache();

  // Force isStaff to true for testing
  const isStaff = true;

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<AttributeGroup | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<GroupFormValues>({
    name: '',
    order: 0,
    items: [],
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [, setAvailableAttributes] = useState<Attribute[]>([]);
  const [selectedAttribute, setSelectedAttribute] = useState<number | null>(null);

  // Configure DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Fetch attribute groups
  const {
    data: groups,
    isLoading: isLoadingGroups,
    error: groupsError,
    refetch: refetchGroups,
  } = useQuery({
    queryKey: ['attributeGroups'],
    queryFn: async () => {
      const response = await axiosInstance.get(paths.attributeGroups.root(), {
        headers: { Accept: 'application/json' },
      });
      return response.data;
    },
    enabled: ENABLE_ATTRIBUTE_GROUPS,
  });

  // Fetch attributes for selecting
  const { data: attributes, isLoading: isLoadingAttributes } = useQuery({
    queryKey: ['attributes'],
    queryFn: async () => {
      const response = await axiosInstance.get(paths.attributes.root(), {
        headers: { Accept: 'application/json' },
      });
      return response.data;
    },
    enabled: ENABLE_ATTRIBUTE_GROUPS,
  });

  // Modify the update mutation to only handle group properties, not items
  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Omit<GroupFormValues, 'items'> & { version?: number };
    }) => {
      // Only send name, order, and version to update group properties
      return await updateAttributeGroup(id, {
        name: data.name,
        order: data.order,
        version: data.version,
      });
    },
    onSuccess: () => {
      invalidateAttributeGroupsCache();
      // Note: Success toast is handled in handleSubmit for better UX
    },
    onError: (error: any) => {
      logError(
        'Failed to update attribute group',
        {
          component: 'AttributeGroupsPage',
          action: 'updateMutation',
          metadata: { error: error.response?.data || error.message },
        },
        { showToast: false, userFacing: false }
      );
      const errorMessage = error.response?.data?.detail || 'Failed to update attribute group';
      toast.error(errorMessage);

      if (error.response?.data) {
        setFormErrors(error.response.data);
      }
    },
  });

  // Create a new mutation for adding attributes to a group
  const addAttributeMutation = useMutation({
    mutationFn: async ({ groupId, attributeId }: { groupId: number; attributeId: number }) => {
      return await addAttributeToGroup(groupId, attributeId);
    },
    onSuccess: () => {
      invalidateAttributeGroupsCache();
      // Note: Success handling is done in the calling function
    },
    onError: (error: any) => {
      logError(
        'Failed to add attribute to group',
        {
          component: 'AttributeGroupsPage',
          action: 'addAttributeMutation',
          metadata: { error: error.response?.data || error.message },
        },
        { showToast: false, userFacing: false }
      );
      const errorMessage = error.response?.data?.detail || 'Failed to add attribute to group';
      toast.error(errorMessage);
    },
  });

  // Create a new mutation for removing attributes from a group
  const removeAttributeMutation = useMutation({
    mutationFn: async ({ groupId, itemId }: { groupId: number; itemId: number }) => {
      return await removeAttributeFromGroup(groupId, itemId);
    },
    onSuccess: () => {
      invalidateAttributeGroupsCache();
      // Note: Success handling is done in the calling function
    },
    onError: (error: any) => {
      logError(
        'Failed to remove attribute from group',
        {
          component: 'AttributeGroupsPage',
          action: 'removeAttributeMutation',
          metadata: { error: error.response?.data || error.message },
        },
        { showToast: false, userFacing: false }
      );
      const errorMessage = error.response?.data?.detail || 'Failed to remove attribute from group';
      toast.error(errorMessage);
    },
  });

  // Create a new mutation for reordering items
  const reorderItemsMutation = useMutation({
    mutationFn: async ({ groupId, itemIds }: { groupId: number; itemIds: number[] }) => {
      return await reorderAttributeGroupItems(groupId, itemIds);
    },
    onSuccess: () => {
      invalidateAttributeGroupsCache();
      // Note: Success handling is done in the calling function
    },
    onError: (error: any) => {
      logError(
        'Failed to reorder attributes in group',
        {
          component: 'AttributeGroupsPage',
          action: 'reorderItemsMutation',
          metadata: { error: error.response?.data || error.message },
        },
        { showToast: false, userFacing: false }
      );
      const errorMessage = error.response?.data?.detail || 'Failed to reorder attributes';
      toast.error(errorMessage);
    },
  });

  // Update the createMutation to use our utility
  const createMutation = useMutation({
    mutationFn: async (data: GroupFormValues) => {
      // First create the group without items
      const { name, order } = data;
      return await createAttributeGroup({ name, order });
    },
    onSuccess: async newGroup => {
      // If there are items to add, add them one by one
      if (formValues.items && formValues.items.length > 0) {
        try {
          await Promise.all(
            formValues.items.map(item => addAttributeToGroup(newGroup.id, item.attribute))
          );

          // After adding all attributes, reorder them if needed
          if (formValues.items.length > 1) {
            const itemIds = formValues.items
              .map(item => item.id)
              .filter(id => id !== undefined) as number[];
            if (itemIds.length > 1) {
              await reorderAttributeGroupItems(newGroup.id, itemIds);
            }
          }
        } catch (error) {
          logError(
            'Failed to add attributes to group',
            {
              component: 'AttributeGroupsPage',
              action: 'createMutation.onSuccess',
              metadata: { error, groupId: newGroup.id },
            },
            { showToast: false, userFacing: false }
          );
          toast.error(
            'Group created but failed to add some attributes. Please try adding them manually.'
          );
          invalidateAttributeGroupsCache();
          return;
        }
      }

      // Only invalidate cache after all operations are complete
      invalidateAttributeGroupsCache();
      toast.success('Attribute group created successfully');
      setIsAddModalOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      logError(
        'Failed to create attribute group',
        {
          component: 'AttributeGroupsPage',
          action: 'createMutation',
          metadata: { error: error.response?.data || error.message },
        },
        { showToast: false, userFacing: false }
      );
      const errorMessage = error.response?.data?.detail || 'Failed to create attribute group';
      toast.error(errorMessage);

      if (error.response?.data) {
        setFormErrors(error.response.data);
      }
    },
  });

  // Update the deleteMutation to use our utility
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      if (!isStaff) {
        toast.error('Read-only tenant');
        throw new Error('Permission denied');
      }

      return await deleteAttributeGroup(id);
    },
    onSuccess: () => {
      invalidateAttributeGroupsCache();
      toast.success('Attribute group deleted successfully');
    },
    onError: (error: any) => {
      logError(
        'Failed to delete attribute group',
        {
          component: 'AttributeGroupsPage',
          action: 'deleteMutation',
          metadata: { error: error.response?.data || error.message },
        },
        { showToast: false, userFacing: false }
      );
      const errorMessage = error.response?.data?.detail || 'Failed to delete attribute group';
      toast.error(errorMessage);
    },
  });

  // Update available attributes when attributes or selected group changes
  useEffect(() => {
    if (attributes && selectedGroupId) {
      const group = groups?.find((g: AttributeGroup) => g.id === selectedGroupId);
      if (group) {
        // Filter out attributes that are already in the group
        const groupAttributeIds = group.items.map((item: AttributeGroupItem) => item.attribute);
        setAvailableAttributes(
          attributes.filter((attr: Attribute) => !groupAttributeIds.includes(attr.id))
        );
      } else {
        setAvailableAttributes(attributes);
      }
    } else if (attributes) {
      setAvailableAttributes(attributes);
    }
  }, [attributes, selectedGroupId, groups]);

  // Reset form values and errors
  const resetForm = () => {
    setFormValues({
      name: '',
      order: 0,
      items: [],
    });
    setFormErrors({});
    setSelectedGroupId(null);
    setSelectedAttribute(null);
  };

  // Handle opening the edit modal
  const handleEdit = (group: AttributeGroup) => {
    setSelectedGroup(group);
    setSelectedGroupId(group.id);
    setFormValues({
      name: group.name,
      order: group.order,
      items: [...group.items], // Create a copy to avoid mutating the original
    });
    setIsEditModalOpen(true);
  };

  // Handle form input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Filter attributes that haven't been assigned yet
  const getUnassignedAttributes = () => {
    if (
      !attributes ||
      !Array.isArray(attributes) ||
      !formValues.items ||
      !Array.isArray(formValues.items)
    ) {
      return [];
    }

    // Get attribute IDs already in the form
    const formAttributeIds = formValues.items.map((item: AttributeGroupItem) => item.attribute);

    return attributes.filter(
      (attr: Attribute) => attr && attr.id && !formAttributeIds.includes(attr.id) // Don't show attributes already in the form
    );
  };

  // Handle removing an attribute from the group
  const handleRemoveAttribute = (attributeId: number) => {
    // Find the attribute to add back to available list
    const attribute = attributes?.find((attr: Attribute) => attr.id === attributeId);
    if (attribute) {
      setAvailableAttributes(prev => (Array.isArray(prev) ? [...prev, attribute] : [attribute]));
    }

    // Remove the item from the form values
    setFormValues(prev => ({
      ...prev,
      items: Array.isArray(prev.items)
        ? prev.items.filter(item => item.attribute !== attributeId)
        : [],
    }));
  };

  // Handle adding an attribute to the group
  const handleAddAttribute = () => {
    if (selectedAttribute && formValues.items) {
      const newItem = {
        attribute: selectedAttribute,
        order: Array.isArray(formValues.items) ? formValues.items.length : 0,
      };

      setFormValues(prev => ({
        ...prev,
        items: Array.isArray(prev.items) ? [...prev.items, newItem] : [newItem],
      }));

      // Update available attributes
      setAvailableAttributes(prev =>
        Array.isArray(prev) ? prev.filter(attr => attr.id !== selectedAttribute) : []
      );
      setSelectedAttribute(null);
    }
  };

  // Handle drag and drop reordering
  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id && formValues.items && Array.isArray(formValues.items)) {
      // Find the indices for the dragged and target items
      const activeIndex = formValues.items.findIndex(
        item => (item.id?.toString() || `new-${item.attribute}`) === active.id
      );

      const overIndex = formValues.items.findIndex(
        item => (item.id?.toString() || `new-${item.attribute}`) === over.id
      );

      if (activeIndex >= 0 && overIndex >= 0) {
        // Reorder the items
        const updatedItems = [...formValues.items];
        const [movedItem] = updatedItems.splice(activeIndex, 1);
        updatedItems.splice(overIndex, 0, movedItem);

        // Update order values
        const reorderedItems = updatedItems.map((item, index) => ({
          ...item,
          order: index,
        }));

        // Update form values
        setFormValues(prev => ({
          ...prev,
          items: reorderedItems,
        }));
      }
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formValues.name.trim()) {
      errors.name = 'Name is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission with proper error handling and serialization
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (selectedGroup && isEditModalOpen) {
      let retryCount = 0;
      const maxRetries = 3;

      while (retryCount < maxRetries) {
        try {
          // Fetch fresh group data to avoid stale state issues and check version
          const freshGroupData = await queryClient.fetchQuery({
            queryKey: ['attributeGroups'],
            queryFn: async () => {
              const response = await axiosInstance.get(paths.attributeGroups.root(), {
                headers: { Accept: 'application/json' },
              });
              return response.data;
            },
          });

          const freshGroup = freshGroupData?.find((g: AttributeGroup) => g.id === selectedGroup.id);
          if (!freshGroup) {
            throw new Error('Attribute group not found. It may have been deleted by another user.');
          }

          // Optimistic locking check - compare versions
          if (
            selectedGroup.version &&
            freshGroup.version &&
            selectedGroup.version !== freshGroup.version
          ) {
            throw new Error(
              `This attribute group has been modified by another user. ` +
                `Expected version ${selectedGroup.version}, found ${freshGroup.version}. ` +
                `Please refresh the page and try again.`
            );
          }

          // Serialize operations to prevent race conditions
          logDebug('Starting attribute group update sequence', {
            context: 'AttributeGroupsPage.handleSubmit',
            groupId: selectedGroup.id,
            step: 'initialization',
          });

          // Step 1: Update basic group properties with version for optimistic locking
          await updateMutation.mutateAsync({
            id: selectedGroup.id,
            data: {
              name: formValues.name,
              order: formValues.order,
              version: selectedGroup.version, // Include version for optimistic locking
            },
          });

          logDebug('Updated group properties', {
            context: 'AttributeGroupsPage.handleSubmit',
            groupId: selectedGroup.id,
            step: 'properties_updated',
          });

          // Step 2: Calculate changes using fresh data
          const originalItemIds = new Set(
            freshGroup.items.map((item: AttributeGroupItem) => item.attribute)
          );
          const newItemIds = new Set(
            formValues.items.map((item: AttributeGroupItem) => item.attribute)
          );

          const itemsToAdd = formValues.items.filter(
            (item: AttributeGroupItem) => !originalItemIds.has(item.attribute)
          );
          const itemsToRemove = freshGroup.items.filter(
            (item: AttributeGroupItem) => !newItemIds.has(item.attribute)
          );

          // Step 3: Handle removals (serialized to prevent conflicts)
          for (const item of itemsToRemove) {
            if (item.id) {
              await removeAttributeMutation.mutateAsync({
                groupId: selectedGroup.id,
                itemId: item.id,
              });
              logDebug('Removed attribute from group', {
                context: 'AttributeGroupsPage.handleSubmit',
                groupId: selectedGroup.id,
                itemId: item.id,
                attributeId: item.attribute,
              });
            }
          }

          // Step 4: Handle additions (serialized to prevent conflicts)
          const createdItemIds: number[] = [];
          for (const item of itemsToAdd) {
            const created = await addAttributeMutation.mutateAsync({
              groupId: selectedGroup.id,
              attributeId: item.attribute,
            });
            if (created?.id) {
              createdItemIds.push(created.id);
            }
            logDebug('Added attribute to group', {
              context: 'AttributeGroupsPage.handleSubmit',
              groupId: selectedGroup.id,
              attributeId: item.attribute,
            });
          }

          // Step 5: Handle reordering if needed (include newly created items)
          const remainingItems = [
            ...freshGroup.items.filter(
              (item: AttributeGroupItem) => newItemIds.has(item.attribute) && item.id
            ),
          ];

          // Append newly created item IDs with attribute mapping order
          createdItemIds.forEach((cid, idx) => {
            const attrId = Array.from(itemsToAdd)[idx]?.attribute;
            if (attrId !== undefined) {
              remainingItems.push({ id: cid, attribute: attrId, order: 0 });
            }
          });

          if (remainingItems.length > 1) {
            const newOrderMap = new Map(
              formValues.items.map((item, index) => [item.attribute, index])
            );

            const sortedItems = [...remainingItems].sort((a, b) => {
              const orderA = newOrderMap.get(a.attribute) ?? 0;
              const orderB = newOrderMap.get(b.attribute) ?? 0;
              return orderA - orderB;
            });

            const itemIds = sortedItems.map(item => item.id).filter(Boolean) as number[];
            if (itemIds.length > 1) {
              // Fetch fresh group data again before reordering
              const reorderGroupData = await queryClient.fetchQuery({
                queryKey: ['attributeGroups'],
                queryFn: async () => {
                  const response = await axiosInstance.get(paths.attributeGroups.root(), {
                    headers: { Accept: 'application/json' },
                  });
                  return response.data;
                },
              });

              const reorderGroup = reorderGroupData?.find(
                (g: AttributeGroup) => g.id === selectedGroup.id
              );
              if (reorderGroup) {
                await reorderItemsMutation.mutateAsync({
                  groupId: selectedGroup.id,
                  itemIds,
                });
                logDebug('Reordered group items', {
                  context: 'AttributeGroupsPage.handleSubmit',
                  groupId: selectedGroup.id,
                  itemIds,
                });
              }
            }
          }

          // Step 6: Final cache refresh and success
          await refreshAllCaches();
          setIsEditModalOpen(false);
          resetForm();
          toast.success('Attribute group updated successfully');

          logDebug('Attribute group update completed successfully', {
            context: 'AttributeGroupsPage.handleSubmit',
            groupId: selectedGroup.id,
            step: 'completed',
          });

          return; // Success - exit retry loop
        } catch (error: any) {
          retryCount++;

          // Check if this is a retryable error
          const isVersionConflict =
            error.message?.includes('Expected version') ||
            error.message?.includes('modified by another user');
          const isRetryable =
            error.message?.includes('not found') ||
            error.response?.status === 404 ||
            error.response?.status === 409; // Conflict

          // Don't retry version conflicts - user needs to refresh
          if (isVersionConflict) {
            const userMessage =
              'This attribute group has been modified by another user. Please refresh the page and try again.';
            toast.error(userMessage);
            break; // Exit retry loop immediately
          }

          logError(
            'Attribute group update failed',
            {
              component: 'AttributeGroupsPage',
              action: 'handleSubmit',
              metadata: {
                error: error.message || error,
                retryCount,
                isRetryable,
                groupId: selectedGroup.id,
              },
            },
            { showToast: false, userFacing: false }
          );

          if (isRetryable && retryCount < maxRetries) {
            // Wait before retry with exponential backoff
            await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retryCount - 1)));
            logDebug('Retrying attribute group update', {
              context: 'AttributeGroupsPage.handleSubmit',
              retryCount,
              groupId: selectedGroup.id,
            });
            continue; // Retry
          } else {
            // Final failure
            const userMessage = error.message?.includes('not found')
              ? 'The attribute group may have been modified by another user. Please refresh and try again.'
              : 'Failed to update attribute group. Please try again.';

            toast.error(userMessage);
            break; // Exit retry loop
          }
        }
      }
    } else {
      // Creation flow remains the same but with better error handling
      try {
        await createMutation.mutateAsync({
          name: formValues.name,
          order: formValues.order,
          items: formValues.items,
        });
      } catch (error: any) {
        logError(
          'Failed to create attribute group',
          {
            component: 'AttributeGroupsPage',
            action: 'handleSubmit',
            metadata: { error: error.message || error },
          },
          { showToast: false, userFacing: false }
        );
        toast.error('Failed to create attribute group. Please try again.');
      }
    }
  };

  // Handle attribute group deletion
  const handleDelete = (group: AttributeGroup) => {
    deleteMutation.mutate(group.id);
  };

  // Comprehensive cache refresh function
  const refreshAllCaches = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['attributeGroups'] }),
      queryClient.invalidateQueries({ queryKey: ['attributes'] }),
      queryClient.refetchQueries({ queryKey: ['attributeGroups'] }),
      refetchGroups(),
    ]);
  };

  if (!ENABLE_ATTRIBUTE_GROUPS) {
    return null;
  }

  return (
    <div>
      {isLoadingGroups ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-[300px] w-full" />
        </div>
      ) : groupsError ? (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-center p-8 text-red-500">
              <AlertCircle className="mr-2 size-6" />
              <p>{i18n.messages.loadError}</p>
            </div>
            <div className="mt-4 flex justify-center">
              <Button onClick={() => refetchGroups()}>{i18n.actions.retry}</Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="mb-6 flex items-center justify-between">
            <p className="text-sm text-slate-600 dark:text-gray-400">
              {i18n.groupsAvailable(groups?.length || 0)}
            </p>
            {isStaff && (
              <Button
                onClick={() => {
                  resetForm();
                  setIsAddModalOpen(true);
                }}
                className="bg-teal-600 text-white hover:bg-teal-700"
              >
                <PlusCircle className="mr-2 size-4" />
                {i18n.actions.newGroup}
              </Button>
            )}
          </div>

          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{i18n.table.headers.name}</TableHead>
                  <TableHead>{i18n.table.headers.order}</TableHead>
                  <TableHead>{i18n.table.headers.attributes}</TableHead>
                  <TableHead className="text-right">{i18n.table.headers.actions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groups?.length > 0 ? (
                  groups.map((group: AttributeGroup) => (
                    <TableRow key={group.id}>
                      <TableCell className="font-medium">{group.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{group.order}</Badge>
                      </TableCell>
                      <TableCell>
                        {group.items.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {group.items.slice(0, 3).map(item => {
                              const attribute = attributes?.find(
                                (a: Attribute) => a.id === item.attribute
                              );
                              return attribute ? (
                                <Badge
                                  key={item.id}
                                  variant="outline"
                                  className="border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/50 dark:text-blue-300"
                                >
                                  {attribute.label}
                                </Badge>
                              ) : null;
                            })}
                            {group.items.length > 3 && (
                              <Badge variant="outline">
                                {i18n.moreAttributes(group.items.length - 3)}
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            {i18n.table.noAttributes}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end space-x-2">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEdit(group)}
                                  disabled={!isStaff}
                                  className="hover:bg-slate-100"
                                >
                                  <Pencil className="size-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {isStaff
                                  ? i18n.table.tooltips.editGroup
                                  : i18n.table.tooltips.readOnlyMode}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>

                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    if (isStaff) {
                                      document.getElementById(`delete-dialog-${group.id}`)?.click();
                                    }
                                  }}
                                  disabled={!isStaff}
                                  className="hover:bg-red-50 hover:text-red-600"
                                >
                                  <Trash className="size-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {isStaff
                                  ? i18n.table.tooltips.deleteGroup
                                  : i18n.table.tooltips.readOnlyMode}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                      {i18n.noGroups}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>

          {/* AlertDialogs for delete confirmation */}
          {groups?.map((group: AttributeGroup) => (
            <AlertDialog key={`alert-${group.id}`}>
              <AlertDialogTrigger className="hidden" id={`delete-dialog-${group.id}`}>
                Open
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{i18n.dialogs.deleteTitle}</AlertDialogTitle>
                  <AlertDialogDescription>{i18n.dialogs.deleteDescription}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{i18n.dialogs.deleteCancel}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => handleDelete(group)}
                    className="bg-red-600 text-white hover:bg-red-700"
                  >
                    {i18n.dialogs.deleteConfirm}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ))}
        </>
      )}

      {/* Create Attribute Group Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="flex max-h-[90vh] max-w-lg flex-col">
          <DialogHeader>
            <DialogTitle>{i18n.form.createTitle}</DialogTitle>
            <DialogDescription>{i18n.form.createDescription}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
            <div className="flex-1 space-y-4 overflow-y-auto p-2">
              <div className="space-y-2">
                <Label htmlFor="name">
                  {i18n.form.fields.name}{' '}
                  <span className="text-danger-500">{i18n.form.fields.nameRequired}</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formValues.name}
                  onChange={handleInputChange}
                  placeholder={i18n.form.fields.namePlaceholder}
                  className={formErrors.name ? 'border-danger-500' : ''}
                />
                {formErrors.name && <p className="text-sm text-danger-500">{formErrors.name}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="order">{i18n.form.fields.order}</Label>
                <Input
                  id="order"
                  name="order"
                  type="number"
                  value={formValues.order}
                  onChange={handleInputChange}
                  min={0}
                />
                <p className="text-xs text-muted-foreground">{i18n.form.fields.orderDescription}</p>
              </div>

              <div className="space-y-2 pt-2">
                <Label>{i18n.form.fields.attributesInGroup}</Label>

                {/* Attribute selector */}
                <div className="flex space-x-2">
                  <select
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    value={selectedAttribute || ''}
                    onChange={e => setSelectedAttribute(Number(e.target.value))}
                    disabled={isLoadingAttributes || getUnassignedAttributes().length === 0}
                  >
                    <option value="">{i18n.form.fields.selectAttributeDefault}</option>
                    {getUnassignedAttributes().map((attr: Attribute) => (
                      <option key={attr.id} value={attr.id}>
                        {attr.label} ({attr.data_type})
                      </option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    onClick={handleAddAttribute}
                    disabled={
                      createMutation.isPending || updateMutation.isPending || !selectedAttribute
                    }
                  >
                    {i18n.actions.add}
                  </Button>
                </div>

                {/* Draggable attribute list with scroll */}
                <div className="mt-4 rounded-md border bg-muted/50">
                  <div className="max-h-60 overflow-y-auto p-3">
                    {!formValues.items || formValues.items.length === 0 ? (
                      <p className="py-6 text-center text-sm text-muted-foreground">
                        {i18n.form.fields.noAttributesAdded}
                      </p>
                    ) : (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={(formValues.items || []).map(
                            item => item.id?.toString() || `new-${item.attribute}`
                          )}
                          strategy={verticalListSortingStrategy}
                        >
                          {(formValues.items || []).map(item => (
                            <SortableAttributeItem
                              key={item.id?.toString() || `new-${item.attribute}`}
                              item={item}
                              attributes={attributes || []}
                              onRemove={handleRemoveAttribute}
                            />
                          ))}
                        </SortableContext>
                      </DndContext>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="flex gap-2 border-t pt-4">
              <Button type="button" variant="outline" onClick={() => setIsAddModalOpen(false)}>
                {i18n.actions.cancel}
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending}
                className="bg-teal-600 text-white hover:bg-teal-700"
              >
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    {i18n.actions.saving}
                  </>
                ) : (
                  i18n.actions.save
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Attribute Group Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col">
          <DialogHeader>
            <DialogTitle>{i18n.form.editTitle}</DialogTitle>
            <DialogDescription>{i18n.form.editDescription}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
            <div className="flex-1 space-y-4 p-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">
                  {i18n.form.fields.name}{' '}
                  <span className="text-danger-500">{i18n.form.fields.nameRequired}</span>
                </Label>
                <Input
                  id="edit-name"
                  name="name"
                  value={formValues.name}
                  onChange={handleInputChange}
                  placeholder={i18n.form.fields.namePlaceholder}
                  className={formErrors.name ? 'border-danger-500' : ''}
                />
                {formErrors.name && <p className="text-sm text-danger-500">{formErrors.name}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-order">{i18n.form.fields.order}</Label>
                <Input
                  id="edit-order"
                  name="order"
                  type="number"
                  value={formValues.order}
                  onChange={handleInputChange}
                  min={0}
                />
                <p className="text-xs text-muted-foreground">
                  {i18n.form.fields.orderDescriptionEdit}
                </p>
              </div>

              <div className="space-y-2 pt-2">
                <Label>{i18n.form.fields.attributesInGroup}</Label>

                {/* Attribute selector */}
                <div className="flex space-x-2">
                  <select
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    value={selectedAttribute || ''}
                    onChange={e => setSelectedAttribute(Number(e.target.value))}
                    disabled={isLoadingAttributes || getUnassignedAttributes().length === 0}
                  >
                    <option value="">{i18n.form.fields.selectAttributeDefault}</option>
                    {getUnassignedAttributes().map((attr: Attribute) => (
                      <option key={attr.id} value={attr.id}>
                        {attr.label} ({attr.data_type})
                      </option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    onClick={handleAddAttribute}
                    disabled={
                      createMutation.isPending || updateMutation.isPending || !selectedAttribute
                    }
                  >
                    {i18n.actions.add}
                  </Button>
                </div>

                {/* Draggable attribute list with scroll */}
                <div className="mt-4 rounded-md border bg-muted/50">
                  <div className="max-h-60 overflow-y-auto p-3">
                    {!formValues.items || formValues.items.length === 0 ? (
                      <p className="py-6 text-center text-sm text-muted-foreground">
                        {i18n.form.fields.noAttributesAdded}
                      </p>
                    ) : (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={(formValues.items || []).map(
                            item => item.id?.toString() || `new-${item.attribute}`
                          )}
                          strategy={verticalListSortingStrategy}
                        >
                          {(formValues.items || []).map(item => (
                            <SortableAttributeItem
                              key={item.id?.toString() || `new-${item.attribute}`}
                              item={item}
                              attributes={attributes || []}
                              onRemove={handleRemoveAttribute}
                            />
                          ))}
                        </SortableContext>
                      </DndContext>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="border-t px-4 py-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  resetForm();
                  setIsEditModalOpen(false);
                }}
              >
                {i18n.actions.cancel}
              </Button>
              <Button
                type="submit"
                disabled={updateMutation.isPending}
                className="bg-teal-600 text-white hover:bg-teal-700"
              >
                {updateMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    {i18n.actions.saving}
                  </>
                ) : (
                  i18n.actions.saveChanges
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AttributeGroupsPage;
